import { BOrganizationItem, BSourceInfo, GCountryFlag, GDescription, GTooltip } from '@pharmsnap/shared/components'
import { useLocale } from '@pharmsnap/shared/composition'
import { EMPTY_PLACEHOLDER } from '@pharmsnap/shared/constants'
import { renderLimited } from '@pharmsnap/shared/src/render'
import { IBaseDevStatusCountryInfo, IGDescriptionItemProps } from '@pharmsnap/shared/types'
import { CTRL_C_INFO, formatTimestamp } from '@pharmsnap/shared/utils'
import { computed, defineComponent, getCurrentInstance, PropType, toRefs } from '@vue/composition-api'
import $classes from '../EntityCard.module.scss'
import cn from './locales/cn.json'
import en from './locales/en.json'

export const BDevStatusCard = defineComponent({
  name: 'BDevStatusCard',
  i18n: {
    messages: {
      cn,
      en,
    },
  },
  props: {
    data: {
      type: Object as PropType<IBaseDevStatusCountryInfo>,
      required: true,
    },
    isApproved: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const { data, isApproved } = toRefs(props)
    const { locale, localeUpcase } = useLocale()
    const ins = getCurrentInstance()
    const countryName = computed(() => {
      const country = data.value.country_view
      return country?.alpha_2_code || country?.display_name || ''
    })
    const showApprovedDate = computed(() => isApproved.value && typeof data.value.approved_date !== 'undefined')
    const showWithDrawDate = computed(() => isApproved.value && typeof data.value.withdraw_date !== 'undefined')
    const orgs = computed(() => data.value.organization_info || [])
    const allSource = computed(() => {
      return data.value.organization_info?.flatMap((organizationInfo) => {
        return organizationInfo.data_source || []
      })
    })
    const descItems = computed(() => {
      const countryItem: IGDescriptionItemProps = {
        label: ins?.proxy.$tc('devStatusCard.country') || '',
        field: 'country',
        contentCls: 'flex items-center',
      }
      const orgItem: IGDescriptionItemProps = {
        label: ins?.proxy.$tc('devStatusCard.org') || '',
        field: 'org',
      }

      const items = [countryItem, orgItem]

      if (showApprovedDate.value) {
        items.push({
          label: ins?.proxy.$tc('devStatusCard.firstApprovedDate') || '',
          field: 'firstApprovedDate',
        })
      }

      if (showWithDrawDate.value) {
        items.push({
          label: ins?.proxy.$tc('devStatusCard.withdrawDate') || '',
          field: 'withdrawDate',
        })
      }
      if (allSource.value?.length) {
        items.push({
          label: ins?.proxy.$tc('devStatusCard.source') || '',
          field: 'source',
        })
      }

      return items
    })

    return {
      allSource,
      countryName,
      showApprovedDate,
      showWithDrawDate,
      orgs,
      locale,
      localeUpcase,
      descItems,
    }
  },
  methods: {
    renderInactiveTooltip() {
      return (
        <GTooltip theme="light" mode="icon" iconName="Stop" iconSize={20}>
          <div slot="content">{this.$tc('common.pipelineStop')}</div>
        </GTooltip>
      )
    },
  },
  render() {
    return (
      <div class={[$classes.card, CTRL_C_INFO]}>
        <GDescription
          data={this.data}
          width={this.locale === 'cn' ? 100 : 135}
          size="medium"
          items={this.descItems}
          scopedSlots={{
            country: () => (
              <GCountryFlag
                name={this.countryName}
                nameCn={this.data.country_view?.display_name_cn}
                useFullName={true}
                showName={true}
                showUnknownCountryFlag
              ></GCountryFlag>
            ),
            source: () =>
              this.allSource?.length && (
                <div class="pt-2 overflow-hidden">
                  <BSourceInfo mode="detail" sourceInfo={this.allSource} limitCount={3} limitMode="expand" triggerPoint="DEV_STATUS"></BSourceInfo>
                </div>
              ),
            org: () =>
              renderLimited(this.orgs, {
                limitCount: 3,
                render: (item) => (
                  <div class="flex items-center">
                    <BOrganizationItem data={item.org_id_view} popover={false} isSignature={true} isNameNeedDegrade={true}></BOrganizationItem>
                    {item.inactive ? <span class="flex-shrink-0">{this.renderInactiveTooltip()}</span> : null}
                  </div>
                ),
              }),
            firstApprovedDate: ({ data }: { data: IBaseDevStatusCountryInfo }) =>
              data.approved_date ? formatTimestamp(data.approved_date, { locale: this.localeUpcase }) : EMPTY_PLACEHOLDER,
            withdrawDate: ({ data }: { data: IBaseDevStatusCountryInfo }) =>
              data.withdraw_date ? formatTimestamp(data.withdraw_date, { locale: this.localeUpcase }) : EMPTY_PLACEHOLDER,
          }}
        ></GDescription>
      </div>
    )
  },
})
